import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useApiClients } from '../../../context/ApiClientsContext';
import SphereAvatar from '../../molecules/sphere-avatar/sphere-avatar';
import UploadAvatarDialogMolecule from '../../molecules/user-identity/UploadAvatarDialogMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import ConfirmDialogAtom, { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import { FormSectionAtom } from '../../atoms/FormSection/FormSectionAtom';
import { FormRowAtom } from '../../atoms/FormRow/FormRowAtom';
import { uploadSphereAvatar, deleteSphereAvatar } from '@center-platform/spheres/cache';
import { useTranslation } from 'localization';
import './ProfileOrganism.scss';

/**
 * Profile data structure sourced from the identity vertex (the system of
 * record post-Descope migration — see specs/descope-auth-surface.md Phase 3).
 */
export interface ProfileData {
  name: string;
  email: string;
  phone: string;
  organization?: string;
  organizationControlled?: boolean;
  imageId?: string;
}

export interface ProfileOrganismProps {
  /** Identity to load/edit. Required — the organism no longer resolves it from Redux. */
  identityId: string;
  /** Organization sphere ID, used to display the org name when organization-controlled */
  organizationId?: string;
  /** Callback fired after a successful profile save */
  onProfileSaved?: (profile: ProfileData) => void;
  className?: string;
}

const DEFAULT_PROFILE: ProfileData = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  organizationControlled: false,
};

/**
 * Gremlin's valueMap(true) wraps scalar properties in an array.
 */
function extractGremlinProperty(prop: any): any {
  if (Array.isArray(prop) && prop.length > 0) {
    return prop[0];
  }
  if (typeof prop === 'string' || typeof prop === 'boolean' || typeof prop === 'number') {
    return prop;
  }
  return undefined;
}

/**
 * ProfileOrganism - Displays and allows editing of the caller's identity profile.
 *
 * The identity vertex (graph-service) is the system of record for display name
 * and phone; both are written through `PATCH /identities/:id/profile`, which
 * also writes the display name through to Descope. Avatar upload/remove goes
 * through the `uploadSphereAvatar`/`deleteSphereAvatar` cache wrappers — never
 * the slot endpoints directly (see /standards/frontend/sphere-avatar.md).
 */
const ProfileOrganism: React.FC<ProfileOrganismProps> = ({
  identityId,
  organizationId,
  onProfileSaved,
  className = '',
}) => {
  const { graphClient } = useApiClients();
  const toastRef = useRef<Toast>(null);
  const { t } = useTranslation();

  const [profile, setProfile] = useState<ProfileData>(DEFAULT_PROFILE);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isUploadDialogVisible, setIsUploadDialogVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState<ProfileData>(DEFAULT_PROFILE);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemovingAvatar, setIsRemovingAvatar] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now()); // Force avatar re-render

  // The loading state replaces the whole organism, including the Toast host; a refresh after
  // a save or avatar change must not show it or the success toast unmounts before it is seen.
  const loadedIdentityRef = useRef<string | undefined>(undefined);

  const loadProfile = useCallback(async () => {
    if (!identityId || !graphClient) return;

    const isFirstLoad = loadedIdentityRef.current !== identityId;
    if (isFirstLoad) setIsLoading(true);
    setLoadFailed(false);
    try {
      const identityResponse: any = await graphClient.get(`/identities/${identityId}`);
      const identityData = identityResponse?.identity ?? identityResponse?.data?.identity ?? {};

      const organizationControlled = Boolean(
        extractGremlinProperty(identityData.organization_controlled)
      );

      let organization = '';
      if (organizationControlled && organizationId) {
        try {
          const orgResponse: any = await graphClient.get(`/spheres/${organizationId}`);
          if (orgResponse?.sphere) {
            organization = orgResponse.sphere.properties?.name || orgResponse.sphere.label || '';
          }
        } catch {
          // Organization sphere unavailable — leave the badge blank rather than fail the load.
        }
      }

      const profileData: ProfileData = {
        name: extractGremlinProperty(identityData.display_name) || '',
        email: extractGremlinProperty(identityData.email) || '',
        phone: extractGremlinProperty(identityData.phone) || '',
        organization,
        organizationControlled,
        imageId: extractGremlinProperty(identityData.imageId),
      };

      setProfile(profileData);
      loadedIdentityRef.current = identityId;
    } catch (error) {
      console.error('[Profile] Failed to load the profile:', error);
      // With no profile on screen the failure replaces the organism (with a retry); a failed
      // refresh after a save keeps the last known profile and only reports the failure.
      if (isFirstLoad) {
        setLoadFailed(true);
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: t('profile.error'),
          detail: t('profile.loadError'),
          life: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [identityId, graphClient, organizationId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleEditClick = () => {
    setEditedProfile({ ...profile });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditedProfile({ ...profile });
    setIsEditMode(false);
  };

  const handleFieldChange = (field: keyof ProfileData, value: string) => {
    setEditedProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    if (!identityId || !graphClient) return;

    setIsSaving(true);
    try {
      const body: { displayName?: string; phone?: string } = {};
      if (editedProfile.name.trim() !== profile.name) {
        body.displayName = editedProfile.name.trim();
      }
      if (editedProfile.phone.trim() !== profile.phone) {
        body.phone = editedProfile.phone.trim();
      }

      if (Object.keys(body).length > 0) {
        await graphClient.patch(`/identities/${identityId}/profile`, body);
      }

      toastRef.current?.show({
        severity: 'success',
        summary: t('profile.success'),
        detail: t('profile.updateSuccess'),
        life: 3000,
      });

      setProfile(editedProfile);
      setIsEditMode(false);
      onProfileSaved?.(editedProfile);
      loadProfile();
    } catch (error: any) {
      const isInvalidPhone = error?.response?.data?.error === 'invalid_phone';
      toastRef.current?.show({
        severity: 'error',
        summary: t('profile.updateFailed'),
        detail: isInvalidPhone ? t('profile.invalidPhoneDetail') : t('profile.updateFailedDetail'),
        life: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (event: any) => {
    const file = event.files[0];
    if (!file || !identityId) return;

    try {
      const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedMimeTypes.includes(file.type)) {
        toastRef.current?.show({
          severity: 'error',
          summary: t('profile.invalidFileType'),
          detail: t('profile.invalidFileTypeDetail'),
          life: 3000,
        });
        return;
      }

      await uploadSphereAvatar(graphClient, identityId, file);

      toastRef.current?.show({
        severity: 'success',
        summary: t('profile.success'),
        detail: t('profile.avatarUploadSuccess'),
        life: 3000,
      });
      setIsUploadDialogVisible(false);
      setAvatarKey(Date.now());
      loadProfile();
    } catch (error) {
      console.error('[Profile] Avatar upload failed:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: t('profile.uploadError'),
        detail: t('profile.uploadErrorDetail'),
        life: 3000,
      });
    }
  };

  const handleRemoveAvatar = () => {
    confirmDialogAtom({
      header: t('profile.removeAvatarConfirmHeader'),
      message: t('profile.removeAvatarConfirmMessage'),
      variant: 'danger',
      accept: async () => {
        setIsRemovingAvatar(true);
        try {
          await deleteSphereAvatar(graphClient, identityId);
          toastRef.current?.show({
            severity: 'success',
            summary: t('profile.success'),
            detail: t('profile.avatarRemoveSuccess'),
            life: 3000,
          });
          setAvatarKey(Date.now());
          loadProfile();
        } catch (error) {
          console.error('[Profile] Avatar removal failed:', error);
          toastRef.current?.show({
            severity: 'error',
            summary: t('profile.error'),
            detail: t('profile.avatarRemoveError'),
            life: 3000,
          });
        } finally {
          setIsRemovingAvatar(false);
        }
      },
    });
  };

  if (isLoading) {
    return (
      <div className={`profile-organism ${className}`}>
        <div className="profile-loading" aria-busy="true">
          <output className="profile-loading__announcement">{t('profile.loading')}</output>
          <div className="profile-loading__skeletons" aria-hidden="true">
            <SkeletonAtom shape="circle" className="profile-loading__avatar" />
            <SkeletonAtom count={3} height="var(--spacing-xl)" />
          </div>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className={`profile-organism ${className}`}>
        <EmptyStateAtom
          icon="pi-exclamation-triangle"
          title={t('profile.loadError')}
          description={t('profile.loadErrorDescription')}
          data-testid="profile-load-error"
        >
          <ButtonAtom label={t('common.retry')} variant="secondary" size="small" onClick={loadProfile} />
        </EmptyStateAtom>
      </div>
    );
  }

  return (
    <div className={`profile-organism ${className}`}>
      <Toast ref={toastRef} />
      <ConfirmDialogAtom />

      <FormSectionAtom variant="card" className="profile-card">
        {!isEditMode && profile.organization && (
          <div className="profile-organization-badge-top">
            <i className="pi pi-building" />
            <span>{profile.organization}</span>
          </div>
        )}

        <div className="profile-main-content">
          <div className="profile-avatar-column">
            <button
              type="button"
              className="profile-avatar"
              onClick={() => setIsUploadDialogVisible(true)}
              aria-label={t('profile.uploadPicture')}
            >
              {profile.imageId ? (
                <SphereAvatar key={avatarKey} sphereId={identityId} imageId={profile.imageId} size="xlarge" />
              ) : (
                <span className="profile-identity-circle" aria-hidden="true">
                  <GlyphAtom glyph="lucide:user" />
                </span>
              )}
              <div className="camera-overlay">
                <i className="pi pi-camera"></i>
              </div>
            </button>
            {profile.imageId && (
              <ButtonAtom
                label={t('profile.removeAvatar')}
                variant="text"
                size="small"
                icon="pi pi-trash"
                loading={isRemovingAvatar}
                onClick={handleRemoveAvatar}
                className="profile-remove-avatar-btn"
              />
            )}
          </div>

          <div className="profile-details-column">
            <FormRowAtom className="profile-name-row" wrap>
              {isEditMode ? (
                <>
                  <div className="profile-field profile-name-field">
                    <label htmlFor="profile-display-name" className="profile-field-label">
                      {t('profile.displayName')}
                    </label>
                    <InputAtom
                      id="profile-display-name"
                      value={editedProfile.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      autoComplete="name"
                      className="profile-name-input"
                    />
                  </div>
                  <ButtonAtom
                    label={t('common.actions.save')}
                    icon="pi pi-check"
                    onClick={handleSaveProfile}
                    loading={isSaving}
                    variant="primary"
                    shape="pill"
                    className="profile-save-btn"
                  />
                  <ButtonAtom
                    label={t('common.actions.cancel')}
                    icon="pi pi-times"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    variant="secondary"
                    shape="pill"
                    className="profile-cancel-btn"
                  />
                </>
              ) : (
                <>
                  <h2 className="profile-name">{profile.name || t('profile.nameNotSet')}</h2>
                  {!profile.organizationControlled && (
                    <button
                      type="button"
                      className="profile-edit-icon"
                      aria-label={t('profile.editProfile')}
                      onClick={handleEditClick}
                    >
                      <i className="pi pi-pencil" />
                    </button>
                  )}
                </>
              )}
            </FormRowAtom>

            <FormRowAtom className="profile-contact-row" justify="between" wrap>
              {profile.email && (
                <div className="profile-info-item">
                  <i className="pi pi-envelope" />
                  <span>{profile.email}</span>
                </div>
              )}

              <div className="profile-info-item">
                <i className="pi pi-phone" />
                {isEditMode && (
                  <div className="profile-field profile-phone-field">
                    <label htmlFor="profile-phone" className="profile-field-label">
                      {t('profile.phoneNumber')}
                    </label>
                    <InputAtom
                      id="profile-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={editedProfile.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      className="profile-phone-input"
                    />
                  </div>
                )}
                {!isEditMode && profile.phone && <span>{profile.phone}</span>}
                {!isEditMode && !profile.phone && (
                  <span className="profile-no-value">{t('profile.noPhone')}</span>
                )}
              </div>
            </FormRowAtom>

            {profile.organizationControlled && !isEditMode && (
              <div className="profile-info-item">
                <i className="pi pi-lock" />
                <span>{t('profile.controlledByOrg')}</span>
              </div>
            )}
          </div>
        </div>
      </FormSectionAtom>

      <UploadAvatarDialogMolecule
        visible={isUploadDialogVisible}
        onHide={() => setIsUploadDialogVisible(false)}
        onUpload={handleFileUpload}
      />
    </div>
  );
};

export default ProfileOrganism;
